'use client'

import {
    createMedicalService,
    getMedicalService,
    updateMedicalService,
} from '@/lib/api/medical-services/medical-services-api'
import { mask, unMask } from '@/lib/form-state/util'
import { useAuth } from '@/store/hooks'
import { useEffect, useState } from 'react'
import {
    MdArrowBack,
    MdSave,
} from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

// ==================== Utilitários de moeda ====================

const currencyFormat = (value: string | number | null | undefined, allowEmpty = false) => {
    if (value === null || value === undefined) value = '0'
    const digits = `${value}`.replace(/[^0-9]+/g, '')
    if (allowEmpty && (!digits || digits === '0' || digits === '00' || digits === '000')) return ''
    return mask(digits, '', 'currency', {
        prefix: 'R$ ',
        decimalSeparator: ',',
        groupSeparator: '.',
        precision: 2,
    })
}

const parseCurrencyToCents = (value: string): number => {
    if (!value.trim()) return 0
    const unmaskedValue = unMask(value, 'currency')
    return parseInt(unmaskedValue)
}

const formatCentsToDisplay = (cents: number | null | undefined): string => {
    if (cents === null || cents === undefined || cents === 0) return ''
    return currencyFormat(cents.toString())
}

// ==================== Componente ====================

export default function MedicalServicesFormPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const isEditing = !!id

    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)

    // Campos do formulário
    const [name, setName] = useState('')
    const [description, setDescription] = useState('')
    const [code, setCode] = useState('')
    const [costPrice, setCostPrice] = useState('')
    const [sellingPrice, setSellingPrice] = useState('')
    const [markupPercentage, setMarkupPercentage] = useState('')
    const [maxDiscount, setMaxDiscount] = useState('')
    const [active, setActive] = useState(true)

    useEffect(() => {
        if (isEditing && id) {
            setLoading(true)
            getMedicalService(parseInt(id)).then(res => {
                if (res.success) {
                    const item = res.result
                    setName(item.name)
                    setDescription(item.description || '')
                    setCode(item.code || '')
                    setCostPrice(formatCentsToDisplay(item.costPrice))
                    setSellingPrice(formatCentsToDisplay(item.sellingPrice))
                    setMarkupPercentage(item.markupPercentage != null ? item.markupPercentage.toString() : '')
                    setMaxDiscount(formatCentsToDisplay(item.maxDiscount))
                    setActive(item.active)
                } else {
                    toast.error('Erro ao carregar serviço')
                    navigate('/registrations/medical-services')
                }
            }).finally(() => setLoading(false))
        }
    }, [id, isEditing, navigate])

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!name.trim()) {
            toast.error('Nome é obrigatório')
            return
        }

        setSaving(true)
        try {
            const data: any = {
                name: name.trim(),
                description: description.trim() || undefined,
                code: code.trim() || undefined,
                costPrice: parseCurrencyToCents(costPrice),
                sellingPrice: sellingPrice ? parseCurrencyToCents(sellingPrice) : undefined,
                markupPercentage: markupPercentage ? parseFloat(markupPercentage) : undefined,
                maxDiscount: maxDiscount ? parseCurrencyToCents(maxDiscount) : undefined,
                active,
                companyId: user!.companyId!,
            }

            const response = isEditing
                ? await updateMedicalService(parseInt(id!), data)
                : await createMedicalService(data)

            if (response.success) {
                toast.success(isEditing ? 'Serviço atualizado com sucesso' : 'Serviço criado com sucesso')
                navigate('/registrations/medical-services')
            } else {
                toast.error(response.error || 'Erro ao salvar serviço')
            }
        } catch {
            toast.error('Erro ao salvar serviço')
        } finally {
            setSaving(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">Carregando...</p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 max-w-4xl mx-auto">
            <div className="flex items-center gap-3">
                <button
                    onClick={() => navigate('/registrations/medical-services')}
                    className="btn btn-ghost btn-sm btn-circle"
                >
                    <MdArrowBack className="text-xl" />
                </button>
                <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                    {isEditing ? 'Editar Serviço Médico' : 'Novo Serviço Médico'}
                </h1>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                {/* Identificação */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h2 className="text-lg font-semibold text-base-content mb-4">Identificação</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">Nome *</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered"
                                value={name}
                                onChange={e => setName(e.target.value)}
                                placeholder="Ex: Consulta Cardiológica"
                                required
                            />
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">Código/Referência</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered"
                                value={code}
                                onChange={e => setCode(e.target.value)}
                                placeholder="Código interno"
                            />
                        </div>

                        <div className="form-control md:col-span-2">
                            <label className="label">
                                <span className="label-text font-medium">Descrição</span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered"
                                value={description}
                                onChange={e => setDescription(e.target.value)}
                                placeholder="Descrição do serviço"
                                rows={3}
                            />
                        </div>
                    </div>
                </div>

                {/* Preços */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h2 className="text-lg font-semibold text-base-content mb-4">Preços</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">Preço de Custo *</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered"
                                value={costPrice}
                                onChange={e => setCostPrice(currencyFormat(e.target.value, true))}
                                placeholder="R$ 0,00"
                            />
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">Preço de Venda</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered"
                                value={sellingPrice}
                                onChange={e => setSellingPrice(currencyFormat(e.target.value, true))}
                                placeholder="R$ 0,00"
                            />
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">Markup (%)</span>
                            </label>
                            <input
                                type="number"
                                className="input input-bordered"
                                value={markupPercentage}
                                onChange={e => setMarkupPercentage(e.target.value)}
                                step="0.01"
                                min="0"
                                placeholder="0,00"
                            />
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">Desconto Máximo</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered"
                                value={maxDiscount}
                                onChange={e => setMaxDiscount(currencyFormat(e.target.value, true))}
                                placeholder="R$ 0,00"
                            />
                        </div>
                    </div>
                </div>

                {/* Status */}
                {isEditing && (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                        <h2 className="text-lg font-semibold text-base-content mb-4">Status</h2>
                        <div className="form-control">
                            <label className="label cursor-pointer justify-start gap-3">
                                <input
                                    type="checkbox"
                                    className="checkbox checkbox-secondary"
                                    checked={active}
                                    onChange={e => setActive(e.target.checked)}
                                />
                                <span className="label-text font-medium">Ativo</span>
                            </label>
                        </div>
                    </div>
                )}

                {/* Botões */}
                <div className="flex items-center justify-end gap-3">
                    <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => navigate('/registrations/medical-services')}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary gap-2"
                        disabled={saving}
                    >
                        {saving ? (
                            <span className="loading loading-spinner loading-sm"></span>
                        ) : (
                            <MdSave className="text-xl" />
                        )}
                        {isEditing ? 'Salvar Alterações' : 'Criar Serviço'}
                    </button>
                </div>
            </form>
        </div>
    )
}
